import { openFoodModule } from './foodModule';
import { hasPaidFoodAccess } from './foodAccess';
import { isPendingPayment } from './subscriptionAccess';
import { futbolDestForUser, openFutbolModule } from './futbolModule';

const HOSTS = new Set(['d28d', 'food', 'training', 'futbol']);
const ENTRY_HOST_KEY = 'd28d_entry_host';
const TRAINING_BRAND_KEY = 'training_brand_slug';

const MODULE_HOST_PREFIX = /^(training|foodplan|escuelasdeportivas|futbol)\./;

export function detectCurrentEntryHost(hostname) {
  const host = String(hostname || (typeof window !== 'undefined' ? window.location.hostname : ''))
    .replace(/^www\./, '')
    .toLowerCase();
  if (host.startsWith('foodplan.') || host === 'foodplan.ecosistemafit.app') return 'food';
  if (host.startsWith('training.') || host === 'training.ecosistemafit.app') return 'training';
  if (host.startsWith('escuelasdeportivas.') || host.startsWith('futbol.')) return 'futbol';
  if (typeof window !== 'undefined' && window.location.port === '3000' && /localhost|127\.0\.0\.1/.test(host)) {
    return 'food';
  }
  if (typeof window !== 'undefined' && window.location.port === '3004' && /localhost|127\.0\.0\.1/.test(host)) {
    return 'training';
  }
  if (typeof window !== 'undefined' && window.location.port === '3007' && /localhost|127\.0\.0\.1/.test(host)) {
    return 'futbol';
  }
  return 'd28d';
}

function shellPublicOrigin() {
  const baked = String(import.meta.env.VITE_SHELL_PUBLIC_URL || '').replace(/\/$/, '');
  if (baked) return baked;
  if (typeof window === 'undefined') return '';
  const { protocol, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:3002`;
  }
  const base = hostname.replace(/^www\./, '');
  if (MODULE_HOST_PREFIX.test(base)) {
    return `${protocol}//${base.replace(MODULE_HOST_PREFIX, '')}`;
  }
  return `${protocol}//${hostname}`;
}

function skipFoodDoor(user) {
  if (!user) return true;
  if (hasPaidFoodAccess(user)) return false;
  const st = String(user.subscription?.estado || '').toLowerCase();
  return Boolean(user.subscription?.pending) || isPendingPayment(st);
}

/**
 * Si el usuario está en el host equivocado, SSO/redirect a la puerta canónica.
 * @returns {Promise<boolean>} true si se disparó navegación
 */
export async function maybeRedirectEntryHost(user) {
  if (!user || typeof window === 'undefined') return false;
  const want = String(user.entry_host || '').toLowerCase();
  if (!HOSTS.has(want)) return false;
  const current = detectCurrentEntryHost();
  if (want === current) return false;
  try {
    if (sessionStorage.getItem('entry_host_redirecting') === '1') return true;
    sessionStorage.setItem('entry_host_redirecting', '1');
  } catch { /* noop */ }

  if (want === 'food') {
    if (skipFoodDoor(user)) {
      try { sessionStorage.removeItem('entry_host_redirecting'); } catch { /* noop */ }
      return false;
    }
    const result = await openFoodModule('/dashboard', { user, standalone: true });
    if (result?.ok || result?.redirecting) return true;
    try { sessionStorage.removeItem('entry_host_redirecting'); } catch { /* noop */ }
    window.location.replace(modulePublicLoginUrl('food'));
    return true;
  }
  // Coach/asesorado: chrome = Shell.
  if (want === 'training') {
    try { sessionStorage.removeItem('entry_host_redirecting'); } catch { /* noop */ }
    return false;
  }
  // academia.app: SSO Nest si login con flag o puerta Academia (misma cuenta shell → Nest).
  if (want === 'futbol') {
    let autoSso = false;
    let doorSso = false;
    try {
      autoSso = sessionStorage.getItem('futbol_auto_sso') === '1';
      doorSso = sessionStorage.getItem('futbol_door_sso') === '1';
    } catch { /* noop */ }
    if (!autoSso && !doorSso) {
      try { sessionStorage.removeItem('entry_host_redirecting'); } catch { /* noop */ }
      return false;
    }
    try {
      sessionStorage.removeItem('futbol_auto_sso');
      sessionStorage.removeItem('futbol_door_sso');
      sessionStorage.setItem('futbol_sso_sent', '1');
    } catch { /* noop */ }
    const result = await openFutbolModule('/dashboard', futbolDestForUser(user));
    if (result?.ok) return true;
    try { sessionStorage.removeItem('entry_host_redirecting'); } catch { /* noop */ }
    return false;
  }
  if (want === 'd28d') {
    const dest = `${shellPublicOrigin()}/`;
    try {
      const here = window.location.origin.replace(/\/$/, '');
      const there = new URL(dest).origin.replace(/\/$/, '');
      if (here === there && (window.location.pathname === '/' || window.location.pathname === '')) {
        sessionStorage.removeItem('entry_host_redirecting');
        return false;
      }
    } catch { /* noop */ }
    window.location.replace(dest);
    return true;
  }
  return false;
}

function rolesOf(user) {
  return [user?.rol, ...(Array.isArray(user?.roles) ? user.roles : [])].filter(Boolean);
}

/** Super admin / admin plataforma: siempre puerta Ecosistema Fit, nunca Training. */
export function platformDoorHost(user) {
  const roles = rolesOf(user);
  if (roles.includes('super_admin') || roles.includes('admin_d28d')) return 'd28d';
  const host = String(user?.entry_host || '').toLowerCase();
  return HOSTS.has(host) ? host : '';
}

export function rememberEntryHost(user) {
  const host = platformDoorHost(user) || String(user?.entry_host || '').toLowerCase();
  if (!HOSTS.has(host)) return;
  try { localStorage.setItem(ENTRY_HOST_KEY, host); } catch { /* noop */ }
  if (host === 'training') rememberTrainingBrand(user);
  else clearRememberedTrainingBrand();
}

function slugifyBrandClient(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function rememberTrainingBrand(user) {
  const b = user?.coach_branding || user?.trainer || {};
  const slug = String(b.brand_slug || '').trim()
    || slugifyBrandClient(b.brand_name || b.nombre || '');
  if (!slug) return;
  try { localStorage.setItem(TRAINING_BRAND_KEY, slug); } catch { /* noop */ }
}

export function peekTrainingBrandSlug() {
  try { return String(localStorage.getItem(TRAINING_BRAND_KEY) || '').trim(); } catch { return ''; }
}

export function peekRememberedEntryHost() {
  try {
    const host = String(localStorage.getItem(ENTRY_HOST_KEY) || '').toLowerCase();
    return HOSTS.has(host) ? host : 'd28d';
  } catch {
    return 'd28d';
  }
}

export function clearRememberedEntryHost() {
  try { localStorage.removeItem(ENTRY_HOST_KEY); } catch { /* noop */ }
}

export function clearRememberedTrainingBrand() {
  try { localStorage.removeItem(TRAINING_BRAND_KEY); } catch { /* noop */ }
}

function isUsableModuleOrigin(url) {
  const raw = String(url || '').trim().replace(/\/$/, '');
  if (!raw || raw.startsWith('/') || raw.includes('/api')) return '';
  if (/foodplan\.tech/i.test(raw)) return '';
  try {
    const u = new URL(raw);
    if (!/^https?:$/.test(u.protocol)) return '';
    return raw;
  } catch {
    return '';
  }
}

function localDoorOrigin(kind) {
  if (typeof window === 'undefined') return '';
  const { protocol, hostname } = window.location;
  const local = hostname === 'localhost' || hostname === '127.0.0.1';
  // Local siempre :3000 / :3004 / :3007 (ignora VITE_* foodplan.tech del .env).
  if (local) {
    const port = kind === 'training' ? '3004' : kind === 'futbol' ? '3007' : '3000';
    return `${protocol}//${hostname}:${port}`;
  }
  const baked = isUsableModuleOrigin(
    kind === 'training'
      ? import.meta.env.VITE_TRAINING_MODULE_URL
      : kind === 'futbol'
        ? import.meta.env.VITE_FUTBOL_MODULE_URL
        : import.meta.env.VITE_FOOD_MODULE_URL,
  );
  if (baked) {
    try {
      const host = new URL(baked).hostname.replace(/^www\./, '').toLowerCase();
      const here = hostname.replace(/^www\./, '').toLowerCase();
      if (!/localhost|127\.0\.0\.1/.test(host) && host !== here) return baked;
    } catch { /* derive */ }
  }
  const base = hostname.replace(/^www\./, '').replace(MODULE_HOST_PREFIX, '');
  if (kind === 'training') return `${protocol}//training.${base}`;
  if (kind === 'futbol') return `${protocol}//escuelasdeportivas.${base}`;
  return `${protocol}//foodplan.${base}`;
}

/** Login público del módulo. force_logout: no reutilizar sesión previa en :3000/:3004/:3007. */
export function modulePublicLoginUrl(kind) {
  if (kind === 'training') return `${localDoorOrigin('training')}/login?force_logout=1`;
  if (kind === 'food') return `${localDoorOrigin('food')}/login?force_logout=1`;
  if (kind === 'futbol') return `${localDoorOrigin('futbol')}/access?force_logout=1`;
  return `${shellPublicOrigin()}/`;
}

/** URL de login de la puerta del usuario (Training / Food / Ecosistema Fit). */
export function loginUrlForEntryHost(host, { idle = false } = {}) {
  let door = HOSTS.has(host) ? host : peekRememberedEntryHost();
  // academia.app no es puerta de re-login del shell (idle/logout → Fit Live).
  if (door === 'futbol') door = 'd28d';
  let url = '/';
  if (typeof window !== 'undefined') {
    if (door === 'training') {
      url = `${localDoorOrigin('training')}/login`;
      const org = peekTrainingBrandSlug();
      if (org) url = `${url}?org=${encodeURIComponent(org)}`;
    } else if (door === 'food') {
      url = `${localDoorOrigin('food')}/login`;
    } else {
      // Ecosistema Fit (local :3002). Nunca origen Training/Food ni /login GYM VIRTUAL.
      url = `${shellPublicOrigin()}/`;
    }
  }
  if (idle) {
    const join = url.includes('?') ? '&' : '?';
    url = `${url}${join}reason=idle`;
  }
  return url;
}

function normalizedPath(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '');
  return p || '/';
}

export function redirectToDoorLogin({ idle = false, host } = {}) {
  if (typeof window === 'undefined') return;
  const door = String(host || '').toLowerCase();
  const url = loginUrlForEntryHost(HOSTS.has(door) ? door : peekRememberedEntryHost(), { idle });
  try {
    const target = new URL(url, window.location.origin);
    const samePlace = window.location.origin === target.origin
      && normalizedPath(window.location.pathname) === normalizedPath(target.pathname);
    const hereOrg = new URLSearchParams(window.location.search).get('org') || '';
    const wantOrg = target.searchParams.get('org') || '';
    const hereIdle = new URLSearchParams(window.location.search).get('reason') === 'idle';
    const wantIdle = target.searchParams.get('reason') === 'idle';
    if (samePlace && hereOrg === wantOrg && hereIdle === wantIdle) return;
  } catch { /* noop */ }
  window.location.replace(url);
}
